/**
 * **Exploding Kittens 的 `S3Rules`** —— `core/` 与 `net/` 之间的那张皮。
 *
 * 这一层是**唯一**认识具体游戏的地方。往上是通用的 Session（不认识牌），
 * 往下是纯规则（不认识网络）。所以它要做的事情少而明确：
 *
 * | 职责 | 一句话 |
 * | --- | --- |
 * | `createInitialState` | 开局：走**和热座完全相同**的动作序列 |
 * | `project` | 转发给 `core/project` —— 脱敏规则只有一份 |
 * | `reduce` | **先验明正身**（动作自称的 actor ≠ 网络层认定的就拒），再转发 |
 * | `prepare` | 结算前问被抽的人要一次"最终手牌顺序" |
 * | `planTimeout` | 房主侧的时序策略：什么时候关窗、什么时候替掉线的人决定 |
 *
 * ## 为什么开局要走动作序列，而不是直接造一个 `GameState`
 *
 * 和 `src/hotseat.ts` 同一个理由：直接拼状态会让联机多出一条**绕过校验**的
 * 旁路 —— 一旦 reducer 有 bug，联机这边会"看起来正常"，而问题出在别处。
 * 走 `lobby/join` ×N → `game/start` 则保证：**能开出局，就一定是合法的局**。
 *
 * ## 时序策略为什么住在这里
 *
 * `architecture.md` §3.4 那条硬约束是「`GameState` 里不许有时间字段」，
 * 所以"什么时候关窗"不能由 reducer 决定，只能由**房主**决定 ——
 * 而那个决定本身也是一个动作（`window/timeout`），照样进棋谱。
 * `planTimeout` 就是那个决定的出处：每次状态变化问一次，
 * Session 负责拿定时器把动作发出去。
 */

import { z } from 'zod';
import { t } from '../i18n';
import type { ReduceContext } from '../core/actions';
import { type RoomConfig } from '../core/config';
import { playerId, type PlayerId } from '../core/ids';
import { nextU32 } from '../core/rng';
import {
  BOT_PROFILES,
  botActor,
  chooseBotAction,
  type BotProfile,
} from '../bot/policy';

/** 房主代打机器人时的思考延迟（毫秒），和热座一致 */
const BOT_THINK_MS = 450;
import { options } from '../core/options';
import { project, type PlayerView } from '../core/project';
import { reduce as coreReduce } from '../core/reducer';
import type { GameEvent } from '../core/events';
import type { GameAction } from '../core/actions';
import { playNeedsChoice, type GameState } from '../core/state';
import type { CardRegistry } from '../core/pack';
import { createLobbyState, validateConfig, type PlayerSeed } from '../core/setup';
import type { HostIo, S3Rules } from '../net/session';
import { trackDeck } from '../analysis/tracker';
import { collectLore, type RemoteBotTransport, type RemoteTurn } from './remoteBot';
import { GameActionSchema, GameEventSchema, PlayerViewSchema } from './schema';

/**
 * 开局要带进来的东西。
 *
 * ⚠️ 它**不是** `GameState` 的一部分 —— 它是"这一局怎么开始"的参数，
 * 由房主在 `openSession` 时给一次。
 */
export interface EkStartOptions {
  /** 大厅里定下来的游戏规则 */
  readonly config: RoomConfig;
  /** 种子。房主用 `crypto` 生成（`core/` 里不许有随机） */
  readonly seed: number;
  /** peer id → 昵称。大厅收集的 */
  readonly names: Readonly<Record<string, string>>;
  /** 机器人座位（没有 peer，由房主代打） */
  readonly botIds?: readonly string[];
  /**
   * 其中交给**远程 MCP 服务端**出牌的座位。它们仍然是房主代打的 bot，
   * 只是决策问的是本地 WebSocket 那头的 LLM；问不到就回退启发式。
   */
  readonly remoteBotIds?: readonly string[];
}

/**
 * **替掉线的人做决定之前的等待**。
 *
 * `architecture.md` §6.5：「客户端掉线：房主把它标记为离线，轮到它时开一个
 * 较长的超时；超时则自动 pass / 按默认策略抉择」。
 *
 * 20 秒是"人可能只是去倒杯水"和"这局不能永远卡着"之间的取舍。
 */
const OFFLINE_GRACE_MS = 20_000;

/**
 * 出牌动画在画面中心停多久（毫秒）。和客户端 `render/game/playReveal.ts` 的
 * `PLAY_HOLD_MS` 对齐。
 *
 * ⚠️ `export` 只为让 `test/render/playReveal.test.ts` **钉住两边相等** ——
 *    值一个字没改（计划 §11.2）。
 */
export const PLAY_HOLD_MS = 500;

/** 举起之后最多等多久让行动者松手（超了就收起，不损失牌） */
export const ARM_HOLD_MS = 5000;

/** 行动者松手提交后，还要再宽限这么久才真正应用效果（期间仍可打断） */
export const COMMIT_GRACE_MS = 500;
/** 每张断点给窗口延长多少（毫秒）。`export` 同样只为测试钉住两边相等 */
export const NOPE_EXTENSION_MS = 1000;
/** 最多延长几张断点（总延长上限 3s） */
export const MAX_NOPE_EXTENSION = 3;

/** 客户端应答"我的最终手牌顺序"的响应体 */
const StealOrderReplySchema = z.object({ order: z.array(z.number().int().nonnegative()) });

/** 房主问客户端要顺序时用的方法名。**Session 会给它加上会话前缀** */
export const STEAL_ORDER_METHOD = 'steal-order';

/**
 * **线上那一份视图。**
 *
 * ⚠️ 曾经它比 `PlayerView` 多带一份 `yourOptions`（房主替每个客户端算好的
 *    合法动作）。现在**玩家侧不再走 `options()`**：手势由
 *    `ui/screens/tableIntent.ts` 的 `parsePlay` 直接从视图推导成意图，
 *    合法性只由 reducer 兜底。`options()` 只留给机器人 / 掉线兜底。
 *
 * 所以视图就是 `core/project` 的输出本身 —— 名字留着，免得动一大片
 * 类型引用。
 */
export type EkView = PlayerView;

/**
 * **房主侧的时序策略（纯函数）—— 联机 Session 和热座共用同一份。**
 *
 * 联机时由 `S3Session` 用 `setTimeout` 在 `afterMs` 之后发 `action`；
 * 热座没有 Session，就在每帧 ticker 上累计时间，到点发同一个 `action`。
 * 两边共用这一份，截止时间才不会漂。
 *
 * ⚠️ 它**不读时钟**：只回答「现在这个局面，房主该在多久之后发什么动作」。
 *    读时钟是调用方的事（`architecture.md` §3.4：`GameState` 里不许有时间字段）。
 */
export function planHostTimeout(
  state: GameState,
  host: string,
  ctx: ReduceContext,
  /** 这个座位是不是机器人。给了它，目标行动的 0.5s 宽限只对机器人保留 */
  isBot?: (id: PlayerId) => boolean,
): { readonly afterMs: number; readonly action: GameAction } | null {
  const me = playerId(host);

  // ---- ① 打断窗口：由出牌动画 + 打断计数决定 ----
  //
  // ★ 关窗是内部机制：deadline = 出牌动画 + 每张断点 1s（上限 3s）。
  //    每来一张断点，状态变化 → 重新问一遍 → 定时器重排（自然实现 +1s）。
  if (state.resolution !== null) {
    const extension = Math.min(state.resolution.nopeChain.length, MAX_NOPE_EXTENSION);
    if (!state.resolution.committed) {
      // 举起阶段：给行动者一点时间松手；超了就收起（牌还在手里，不损失）
      return {
        afterMs: ARM_HOLD_MS,
        action: { t: 'window/timeout', actor: me },
      };
    }
    // ★ **需要玩家做选择的行动不留 0.5s 宽限** —— 选目标 / 选对手的牌 /
    //   选牌名 / 从弃牌堆挑，任一种都算（见 `core/state.ts` 的 `playNeedsChoice`）；
    //   选择本身已经给了反应时间，再等半秒只是拖慢。机器人例外：它选定之后
    //   仍要等够 0.5s，好让目标高亮（屏幕边缘光 + 方块粒子）看得见（用户）。
    const needsChoice = playNeedsChoice(state.resolution.play);
    const botGrace = needsChoice && isBot?.(state.resolution.play.player) === true;
    const grace = !needsChoice || botGrace ? COMMIT_GRACE_MS : 0;
    // 已提交：出牌动画 + 宽限 + 每来一张断点 +1s
    return {
      afterMs: PLAY_HOLD_MS + grace + extension * NOPE_EXTENSION_MS,
      action: { t: 'window/timeout', actor: me },
    };
  }

  // ---- ② 掉线的人欠一个决定 ----
  const decision = state.decision;
  if (decision !== null) {
    const who = state.players.find((p) => p.id === decision.player);
    if (who !== undefined && !who.connected) {
      const fallback = options(state, decision.player, ctx)[0];
      if (fallback !== undefined) {
        return { afterMs: OFFLINE_GRACE_MS, action: fallback.action };
      }
    }
    return null;
  }

  // ---- ③ 掉线的人欠一个回合 ----
  const turn = state.turn;
  if (turn !== null) {
    const who = state.players.find((p) => p.id === turn.player);
    if (who !== undefined && !who.connected) {
      const fallback = options(state, turn.player, ctx)[0];
      if (fallback !== undefined) {
        return { afterMs: OFFLINE_GRACE_MS, action: fallback.action };
      }
    }
  }
  return null;
}

export function createEkRules(
  registry: CardRegistry,
  remoteBots: RemoteBotTransport | null = null,
): S3Rules<GameState, EkView, GameAction, GameEvent> {
  const ctx: ReduceContext = { registry };
  /** 机器人座位 + 它们的随机源（种子化，整局可复现） */
  let botIds: ReadonlySet<string> = new Set();
  /** 其中走远程 MCP 的座位 */
  let remoteBotIds: ReadonlySet<string> = new Set();
  let botRngState = { seed: 1 };
  let remoteTurnSeq = 0;

  /**
   * 远程机器人的等待上限。LLM 往返慢，但不能拖死整桌；超时就回退启发式。
   * 没连上时 `decide` 立刻返回 null，所以这个值只在真的在等 LLM 时生效。
   */
  const REMOTE_DECIDE_MS = 20_000;

  /**
   * 把当前局面推给 MCP，换回一个合法动作序号，再映射成动作。
   * 任何异常 / 超时 / 越界都返回 null，让调用方沿用启发式动作。
   */
  /** 该座位视角的一份快照（回合与事件推送共用） */
  function remoteView(state: GameState, seat: string) {
    const id = playerId(seat);
    const view = project(state, id);
    return {
      seat,
      seatName: view.players.find((player) => player.id === id)?.name ?? seat,
      view,
      analysis: trackDeck(view, registry),
      lore: collectLore(registry.cards),
    };
  }

  async function askRemoteBot(
    transport: RemoteBotTransport,
    state: GameState,
    seat: string,
  ): Promise<GameAction | null> {
    const id = playerId(seat);
    const legal = options(state, id, ctx);
    if (legal.length === 0) return null;
    remoteTurnSeq += 1;
    const turn: RemoteTurn = {
      id: `mcp-${remoteTurnSeq}`,
      ...remoteView(state, seat),
      options: legal,
    };
    let index: number | null;
    try {
      index = await transport.decide(turn, REMOTE_DECIDE_MS);
    } catch {
      return null;
    }
    if (index === null || !Number.isInteger(index) || index < 0 || index >= legal.length) return null;
    const chosen = legal[index];
    return chosen === undefined ? null : chosen.action;
  }
  const botRandom = (): number => {
    const step = nextU32(botRngState);
    botRngState = step.state;
    return step.value / 4294967296;
  };

  return {
    createInitialState(start) {
      const options_ = start.options as EkStartOptions | null;
      if (options_ === null) throw new Error(t('host.rules.missingStartOptions'));
      botIds = new Set(options_.botIds ?? []);
      remoteBotIds = new Set(options_.remoteBotIds ?? []);
      botRngState = { seed: options_.seed >>> 0 };

      const host = playerId(start.host);
      const names = options_.names;
      const config = options_.config;

      const players: PlayerSeed[] = start.players.map((id) => ({
        id: playerId(id),
        // 大厅里没登记的（不该发生）退回 peer id 前 6 位，别让名字变成空字符串
        name: names[id] ?? id.slice(0, 6),
      }));

      const check = validateConfig(config, registry);
      if (!check.ok) {
        throw new Error(t('host.rules.startFailed', { problems: check.problems.map((p) => p.message).join(t('common.problemSeparator')) }));
      }

      // —— 走**和热座完全一样**的那条路：`lobby/join` ×N → `game/start` ——
      // 唯一的区别是"谁加入"由 Session 的名册决定，而不是本地列表
      let state = createLobbyState({ host, config });
      const step = (action: GameAction): void => {
        const result = coreReduce(state, action, ctx);
        if (!result.ok) {
          // ⚠️ 报错里带上**当时的名册**：开局失败最常见的原因是名册里
          //    有重复或空 id，而只报一句"你已经在房间里了"根本看不出是谁
          const roster = players.map((p) => `${p.id}${p.id === host ? '(host)' : ''}`).join(', ');
          throw new Error(
            t('host.rules.startFailedAction', {
              action: action.t,
              reason: result.reason,
              host,
              roster,
            }),
          );
        }
        state = result.state;
      };
      for (const player of players) {
        step({ t: 'lobby/join', actor: player.id, name: player.name });
      }
      step({ t: 'game/start', actor: host, seed: options_.seed >>> 0 });

      return state;
    },

    project: (state, viewer) => {
      const me = playerId(viewer);
      return project(state, me);
    },

    /**
     * ★ **验明正身，再转发。**
     *
     * 动作里有一个自称的 `actor`。网络层认定的身份是 `meta.actor`（`ctx.from`）。
     * 两者不一致就拒 —— 否则任何人都能替别人出牌、替别人抽牌。
     *
     * 本项目明确不做反作弊（`docs/archive/ans.md` Q3），但这条不是反作弊：
     * 它是**鲁棒性** —— 一个错位的客户端（比如两个标签页串了状态）
     * 会以别人的名义动作，而那种 bug 在棋盘上表现为"莫名其妙轮到我"，
     * 极难查。挡住它只要一行。
     */
    reduce(state, action, meta) {
      /**
       * ⚠️ 在线状态（`net/connected` / `net/disconnected`）是**房主**的网络事实，
       * 不由动作自称的 actor 决定；其余动作仍然"自称的 actor ≠ 网络层认定的就拒"。
       */
      const presence = action.t === 'net/connected' || action.t === 'net/disconnected';
      if (presence) {
        if (meta.actor !== state.host) return { ok: false, reason: t('host.rules.hostOnlyPresence') };
      } else if (action.actor !== meta.actor) {
        // ★ 例外：房主可以替**机器人座位**落动作 —— 它们没有 peer，由房主代打。
        //    这条权限只放给机器人；真人之间仍然验明正身。
        const seat = action.actor as unknown as string;
        const isBotSeat = botIds.has(seat) || remoteBotIds.has(seat);
        if (!isBotSeat || meta.actor !== state.host) {
          return {
            ok: false,
            reason: t('host.rules.actorMismatch', { claimed: action.actor, actual: meta.actor }),
          };
        }
      }
      const result = coreReduce(state, action, ctx);
      if (!result.ok) return { ok: false, reason: result.reason };
      return { ok: true, state: result.state, events: result.events };
    },

    isOver: (state) => state.winner !== null,

    /**
     * ★ **结算前的询问：两张同名，谁在哪个位次。**
     *
     * 抽牌人发来的 `decide` 只有一个 `index`（第几位），而
     * "第几位是哪张牌"只有**被抽的人自己**知道 ——
     * 联机时手牌顺序是本机的事，平时一个字节都不同步
     * （评审：「被抽牌人无需向任何人同步他的手牌顺序」）。
     *
     * 所以房主在这一刻问一次，然后把**顺序**塞进动作里。
     * 校验（是不是那一手牌的排列）在 reducer 里做 —— 房主不重复判一遍。
     */
    async prepare(state, action, io: HostIo) {
      // ★ 远程机器人（MCP）：先让它选。选不出 / 超时就沿用传入的启发式动作。
      let resolved: GameAction = action;
      if (remoteBots !== null && remoteBotIds.has(action.actor as unknown as string)) {
        const picked = await askRemoteBot(remoteBots, state, action.actor as unknown as string);
        if (picked !== null) resolved = picked;
      }

      if (resolved.t !== 'decide' || resolved.decision.kind !== 'stealCard') return resolved;
      if (resolved.decision.order !== undefined) return resolved; // 已经带了（热座/重放）

      const pending = state.decision;
      if (pending === null || pending.kind !== 'stealCard') return resolved;

      const victim = pending.from as unknown as string;
      // 被抽的人还在不在？**而且他得在线** —— 离线时问不到，按房主手里那份顺序结算
      const who = state.players.find((p) => (p.id as unknown as string) === victim);
      if (who === undefined || !who.connected) return resolved;

      try {
        const reply = await io.request(victim, STEAL_ORDER_METHOD, {}, StealOrderReplySchema);
        return {
          ...resolved,
          decision: { ...resolved.decision, order: reply.order as never },
        };
      } catch (error) {
        /**
         * ★ **问不到顺序（超时 / 链路抖）就按房主手里那份结算，别把整次抽牌拒掉。**
         *
         * 之前这里直接抛 → applyAction 返回失败 → 客户端把手牌退回去。玩家看到
         * 的是"点了那张牌背但没反应"（用户报的"有概率点不了对手牌堆"）。房主
         * 那份顺序也是一个合法排列，按位次取牌照样成立，只是不保证和对方屏幕上
         * 的一致 —— 比"这次抽牌整个失败"好得多。
         */
        console.warn(`[s3] ${t('host.rules.stealOrderFallback')}`, error);
        return resolved;
      }
    },

    /**
     * ★ **把刚发生的事件推给远程 MCP，实现「别人动了也唤醒 LLM」。**
     *
     * 自己（机器人座位）的动作不必回推 —— 它刚做完，早知道。事件的公开性由
     * `core/events.ts` 保证（那里明文规定事件不得携带隐藏信息）。
     */
    observe(state, action, events) {
      if (remoteBots === null || typeof remoteBots.signal !== 'function') return;
      if (events.length === 0) return;
      const actor = action.actor as unknown as string;
      for (const seat of remoteBotIds) {
        if (seat === actor) continue;
        remoteBots.signal({ ...remoteView(state, seat), events });
      }
    },

    /**
     * ★ **房主侧的时序策略。**
     *
     * 两件事，优先级从高到低：
     *
     * 1. **打断窗口开着** → 到点关窗（`window/timeout`）。
     *    举起阶段 = `ARM_HOLD_MS`；提交后 = 出牌动画（`PLAY_HOLD_MS`）
     *    + **0.5s 宽限**（`COMMIT_GRACE_MS`）+ 每张断点 1s（上限 3s）。
     *    **不叠加 RTO** —— 那会让节奏太拖沓（用户要求）。
     * 2. **等一个掉线的人做决定** → 给一段宽限，然后替他按默认策略决定
     *
     * ⚠️ "默认策略"用的是 `options()` 里的**第一条** ——
     * 那是规则层自己枚举出来的合法动作，所以默认决定**一定是合法的**。
     * 不自己写一套"该出哪张牌"的判断，那种判断迟早和规则对不上。
     */
    /**
     * ★ **连接状态由房主统一落成动作。**
     *
     * Session 在 peer 加入 / 离开 / 心跳判死时调它，把网络事实变成
     * `net/connected` / `net/disconnected`，这样 `connected` 才真的会变，
     * `planTimeout` 的掉线兜底（②/③）才有意义。
     */
    presenceAction(player, present) {
      return {
        t: present ? 'net/connected' : 'net/disconnected',
        actor: playerId(player),
      };
    },

    planTimeout(state, host) {
      return planHostTimeout(state, host, ctx, (id) => botIds.has(id) || remoteBotIds.has(id));
    },

    /**
     * **房主代打机器人。** 只有"现在该动的人"是机器人才返回计划；
     * `afterMs` 用一个短思考延迟（和热座一致），这样联机观众能看到它在动。
     */
    botAction(state) {
      if (botIds.size === 0 && remoteBotIds.size === 0) return null;
      const actor = botActor(state, ctx, botRandom);
      if (actor === undefined || !(botIds.has(actor) || remoteBotIds.has(actor))) return null;
      const action = chooseBotAction({
        state,
        actor: playerId(actor),
        ctx,
        profile: BOT_PROFILES[0] as BotProfile,
        random: botRandom,
      });
      if (action === undefined) return null;
      return { afterMs: BOT_THINK_MS, action };
    },

    schemas: {
      /**
       * ⚠️ 这里要一次类型断言，理由值得写下来。
       *
       * schema 的**输出类型**是"结构上够用的一份"，而它的字段是普通
       * `string` / `number`；`PlayerView` 上那些字段是 **branded** 类型
       * （`PlayerId` 是 `string & { brand }`）。两者运行期完全一样，
       * 但 TS 不认。
       *
       * 与其给 schema 里每个字段都套一层 `z.custom<PlayerId>()`
       * （那只是把断言摊开写，还让 schema 更难读），不如在这里断一次，
       * 并把话说清楚：**类型的事实来源是 `project()`**，
       * schema 只负责在运行期挡住"结构坏掉"的那一类。
       */
      view: PlayerViewSchema as unknown as z.ZodType<EkView>,
      action: GameActionSchema as unknown as z.ZodType<GameAction>,
      event: GameEventSchema as unknown as z.ZodType<GameEvent>,
    },
  };
}

export { OFFLINE_GRACE_MS };
